import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, UserPlus, Check, Users2 } from 'lucide-react';
import paperImg from '../../assets/artistweb/paper.webp';
import { formatInstagramUrl, formatSpotifyUrl, formatYoutubeUrl } from '../../lib/artistWebService';

/**
 * Reusable ArtistCard Component
 * Driven strictly by real database records from Supabase.
 * Features:
 * - Real supplied torn paper artwork (paper.webp)
 * - 120% scaled social & DSP logos (Instagram +5px down, Spotify -3px up, YouTube unmoved)
 * - 110% scaled profile chevron badge
 * - Strictly layered stacking order (paper z-index: 1, logos z-index: 2, collab z-index: 3, foreground identity z-index: 4)
 * - Zero hardcoded or fabricated artist data
 */
export default function ArtistCard({
  artist,
  isFollowing,
  isFollowPending,
  followerCount = 0,
  isSelf = false,
  onToggleFollow,
  onOpenCollab,
  onAvatarError,
  hasAvatarError = false,
}) {
  const displayName = (artist.stage_name || artist.name || 'Artist').trim();
  const initial = (displayName[0] || 'A').toUpperCase();
  const artistUserId = artist.user_id || artist.id;

  const artistProfileUrl =
    artist.canonical_path ||
    (artist.instagram_username
      ? `/artist/${artist.instagram_username}`
      : `/artist/${artist.slug || artistUserId}`);

  // Resolve star rating strictly from real database field
  const rawStars = Number(artist.star_count);
  const starCount = Number.isFinite(rawStars) && rawStars > 0 ? Math.min(5, Math.max(1, rawStars)) : 0;

  // Resolve bio / tagline lines strictly from real database fields
  const rawBio = (artist.artist_web_about || artist.bio || '').trim();
  const bioLines = rawBio ? rawBio.split('\n').map((l) => l.trim()).filter(Boolean) : [];

  // Has valid avatar
  const hasValidAvatar = Boolean(artist.avatar_url && !hasAvatarError);

  // Genre display text (supports secondary genre) strictly from database
  const genreText = artist.genre
    ? artist.secondary_genre
      ? `${artist.genre.trim().toUpperCase()} · ${artist.secondary_genre.trim().toUpperCase()}`
      : artist.genre.trim().toUpperCase()
    : null;

  // Social links validation: strictly based on real database presence and safe URL normalization
  const instagramUrl = formatInstagramUrl(artist.instagram || artist.raw_instagram);
  const spotifyUrl = formatSpotifyUrl(artist.spotify_link);
  const youtubeUrl = formatYoutubeUrl(artist.youtube);

  const hasSocials = Boolean(instagramUrl || spotifyUrl || youtubeUrl);

  // Format follower count (only format genuine numeric values >= 1000)
  const formattedFollowers =
    typeof followerCount === 'number'
      ? followerCount >= 1000
        ? `${(followerCount / 1000).toFixed(1).replace(/\.0$/, '')}K`
        : `${followerCount}`
      : followerCount;

  return (
    <div className="sx-artist-card">
      {/* 1. Top Right Floating Circular Profile Chevron Badge (110% size) */}
      <Link
        to={artistProfileUrl}
        className="sx-artist-card-chevron-badge"
        title={`View ${displayName}'s profile`}
        aria-label={`View ${displayName}'s profile`}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#d9fb06"
          strokeWidth="3.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 5l7 7-7 7M13 5l7 7-7 7" />
        </svg>
      </Link>

      {/* 2. Inner Background & Supplied Torn Paper Artwork (paper.webp) */}
      <div className="sx-artist-card-bg" aria-hidden="true">
        <img
          src={paperImg || '/paper.webp'}
          alt=""
          className="sx-artist-card-torn-paper-img"
          loading="lazy"
        />
      </div>

      {/* 3. Main Foreground Content (z-index: 4, sits in front of paper) */}
      <div className="sx-artist-card-content">
        {/* Top Section: Avatar + Identity Info */}
        <div className="sx-artist-card-top">
          {/* Circular Avatar */}
          <Link
            to={artistProfileUrl}
            className="sx-artist-card-avatar-link"
            title={`View ${displayName}'s profile`}
          >
            <div className="sx-artist-card-avatar">
              {hasValidAvatar ? (
                <img
                  src={artist.avatar_url}
                  alt={`${displayName} profile photo`}
                  onError={() => onAvatarError && onAvatarError(artist.id)}
                  className="sx-artist-card-avatar-img"
                />
              ) : (
                <span className="sx-artist-card-avatar-initial">{initial}</span>
              )}
            </div>
          </Link>

          {/* Identity Information Column */}
          <div className="sx-artist-card-identity">
            <div className="sx-artist-card-name-row">
              <Link
                to={artistProfileUrl}
                className="sx-artist-card-name"
                title={`View ${displayName}'s profile`}
              >
                {displayName}
              </Link>
              {formattedFollowers !== undefined && formattedFollowers !== null && (
                <span
                  className="sx-artist-card-followers-pill"
                  title={`${formattedFollowers} followers on SenritsuX`}
                >
                  {formattedFollowers}
                </span>
              )}
            </div>

            {/* Role / Tagline lines strictly from database */}
            {bioLines.length > 0 && (
              <div className="sx-artist-card-bio-lines">
                {bioLines.slice(0, 2).map((line, idx) => (
                  <p key={idx} className="sx-artist-card-bio-line" title={line}>
                    {line}
                  </p>
                ))}
              </div>
            )}

            {/* Stars rating strictly from database */}
            {starCount > 0 && (
              <div
                className="sx-artist-card-stars"
                title={`${starCount} Star Artist on SenritsuX`}
                aria-label={`${starCount} stars`}
              >
                {'★'.repeat(starCount)}
              </div>
            )}
          </div>
        </div>

        {/* Middle Section: Genre & City Badges (omitted if database fields are missing) */}
        {(genreText || artist.city) && (
          <div className="sx-artist-card-badges">
            {genreText && (
              <span className="sx-artist-card-genre-badge">{genreText}</span>
            )}
            {artist.city && (
              <span className="sx-artist-card-city-badge">
                <MapPin size={10} color="#d9fb06" className="sx-artist-card-pin-icon" />
                {artist.city.toUpperCase()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 4. Social & DSP Logos (rendered strictly when valid submitted links exist) */}
      {hasSocials && (
        <div
          className="sx-artist-card-social-cluster"
          aria-label="Social links"
          onClick={(e) => e.stopPropagation()}
        >
          {(instagramUrl || spotifyUrl) && (
            <div className="sx-social-cluster-row">
              {/* Instagram (+6% to 27.56px, moved 5px downward) */}
              {instagramUrl && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sx-artist-social-icon sx-social-instagram"
                  title={`${displayName} on Instagram`}
                  aria-label={`${displayName} on Instagram`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg width="27.56" height="27.56" viewBox="0 0 24 24" fill="none">
                    <defs>
                      <radialGradient id={`igGrad_${artist.id}`} cx="30%" cy="107%" r="150%">
                        <stop offset="0%" stopColor="#fdf497" />
                        <stop offset="5%" stopColor="#fdf497" />
                        <stop offset="45%" stopColor="#fd5949" />
                        <stop offset="60%" stopColor="#d6249f" />
                        <stop offset="90%" stopColor="#285AEB" />
                      </radialGradient>
                    </defs>
                    <rect x="2" y="2" width="20" height="20" rx="5.5" fill={`url(#igGrad_${artist.id})`} />
                    <path
                      d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"
                      stroke="#ffffff"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="17.5" cy="6.5" r="1.2" fill="#ffffff" />
                  </svg>
                </a>
              )}

              {/* Spotify (+20% to 31.2px, moved 3px upward) */}
              {spotifyUrl && (
                <a
                  href={spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sx-artist-social-icon sx-social-spotify"
                  title={`${displayName} on Spotify`}
                  aria-label={`${displayName} on Spotify`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg width="31.2" height="31.2" viewBox="0 0 24 24" fill="#1DB954">
                    <circle cx="12" cy="12" r="10" />
                    <path
                      d="M16.5 14.8c-.2.3-.5.4-.8.2-2.2-1.3-5-1.6-8.2-.9-.3.1-.7-.1-.8-.4-.1-.3.1-.7.4-.8 3.6-.8 6.7-.5 9.2 1 .3.2.4.6.2.9zm1.1-2.4c-.2.4-.7.5-1.1.3-2.5-1.5-6.3-2-9.3-1.1-.4.1-.9-.1-1-.5-.1-.4.1-.9.5-1 3.4-1 7.6-.5 10.5 1.2.4.3.5.7.4 1.1zm.1-2.5C14.7 8.2 9.7 8 6.8 8.9c-.5.2-1-.1-1.2-.6-.2-.5.1-1 .6-1.2 3.4-1 8.9-.8 12.4 1.3.4.3.6.9.3 1.3-.3.4-.9.6-1.2.2z"
                      fill="#000000"
                    />
                  </svg>
                </a>
              )}
            </div>
          )}

          {/* YouTube (29px x 20px, unmoved) */}
          {youtubeUrl && (
            <div className="sx-social-cluster-row">
              <a
                href={youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="sx-artist-social-icon sx-social-youtube"
                title={`${displayName} on YouTube`}
                aria-label={`${displayName} on YouTube`}
                onClick={(e) => e.stopPropagation()}
              >
                <svg width="29" height="20" viewBox="0 0 24 18" fill="#FF0000">
                  <path
                    d="M23.5 2.6c-.3-1-1.1-1.8-2.1-2.1C19.5 0 12 0 12 0S4.5 0 2.6.5C1.6.8.8 1.6.5 2.6 0 4.5 0 9 0 9s0 4.5.5 6.4c.3 1 1.1 1.8 2.1 2.1C4.5 18 12 18 12 18s7.5 0 9.4-.5c1-.3 1.8-1.1 2.1-2.1.5-1.9.5-6.4.5-6.4s0-4.5-.5-6.4z"
                  />
                  <polygon points="9.6,12.8 15.8,9 9.6,5.2" fill="#ffffff" />
                </svg>
              </a>
            </div>
          )}
        </div>
      )}

      {/* 5. Bottom Action Buttons: Follow & Collab (z-index: 3, lowered) */}
      <div className="sx-artist-card-actions">
        {isSelf ? (
          <>
            <button
              type="button"
              disabled
              className="sx-artist-btn-follow is-self"
              title="This is your artist profile"
              aria-label="Your profile"
            >
              <span>YOU</span>
            </button>
            <button
              type="button"
              disabled
              className="sx-artist-btn-collab is-self"
              title="You cannot collaborate with yourself"
            >
              <Users2 size={13} />
              <span>Collab</span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onToggleFollow && onToggleFollow(artist)}
              disabled={isFollowPending}
              className={`sx-artist-btn-follow ${isFollowing ? 'active' : ''}`}
              title={isFollowing ? 'Unfollow this artist' : 'Follow this artist'}
              aria-label={isFollowing ? 'Unfollow' : 'Follow'}
            >
              {isFollowPending ? (
                '...'
              ) : isFollowing ? (
                <>
                  <Check size={13} color="#d9fb06" className="sx-check-icon" />
                  <span>FOLLOWING</span>
                </>
              ) : (
                <>
                  <UserPlus size={13} />
                  <span>FOLLOW</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => onOpenCollab && onOpenCollab(artist)}
              className="sx-artist-btn-collab"
              title={`Send collaboration request to ${displayName}`}
            >
              <Users2 size={13} />
              <span>Collab</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
